import React from 'react';
import {
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	GridToolbarDensitySelector,
	GridToolbarQuickFilter,
	useGridApiContext,
	useGridRootProps,
	useGridSelector,
} from '@mui/x-data-grid';
import { Button, Menu, MenuItem, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
// PDF export disabled to reduce bundle size
// import { jsPDF } from 'jspdf';
// import autoTable from 'jspdf-autotable';

/**
 * Custom Grid Toolbar with Excel and PDF export options
 * Replaces the default GridToolbar to avoid 100-row print limitation
 */
export default function CustomGridToolbar() {
	const apiRef = useGridApiContext();
	const rootProps = useGridRootProps();
	const localeText = rootProps.localeText || {};
	const rowSelectionModel = useGridSelector(apiRef, (state) => state.rowSelection);
	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);

	const selectedRowCount = Array.isArray(rowSelectionModel)
		? rowSelectionModel.length
		: rowSelectionModel instanceof Set
		? rowSelectionModel.size
		: rowSelectionModel?.ids instanceof Set
		? rowSelectionModel.ids.size
		: 0;
	const selectedRowsWarning =
		selectedRowCount === 1
			? localeText.toolbarExportSelectedRowWarning ||
			  '1 row selected. Export will include selected rows only.'
			: typeof localeText.toolbarExportSelectedRowsWarning === 'function'
			? localeText.toolbarExportSelectedRowsWarning(selectedRowCount)
			: `${selectedRowCount} rows selected. Export will include selected rows only.`;

	const handleExportClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleClearSelection = () => {
		apiRef.current.setRowSelectionModel([]);
	};

	const handlePrint = () => {
		// Print only the current page to avoid the 100-row MUI limitation
		const state = apiRef.current.state;
		const paginationModel = state.pagination.paginationModel;
		const pageSize = paginationModel.pageSize;
		const page = paginationModel.page;

		// Get rows for current page only
		const visibleRowIds = apiRef.current.getSortedRowIds();
		const startIndex = page * pageSize;
		const endIndex = startIndex + pageSize;
		const currentPageRowIds = visibleRowIds.slice(startIndex, endIndex);

		// Trigger print using MUI's export to print functionality
		apiRef.current.exportDataAsPrint({
			fileName: 'table_print',
			hideToolbar: true,
			hideFooter: true,
			allColumns: false,
			getRowsToExport: () => currentPageRowIds,
		});

		handleClose();
	};

	const handleExportCSV = () => {
		const columns = apiRef.current.getAllColumns();
		const rows = apiRef.current.getSortedRowIds().map((id) => apiRef.current.getRow(id));
		const columnVisibilityModel = apiRef.current.state.columns.columnVisibilityModel;

		// Get visible columns only
		const visibleColumns = columns.filter((col) => {
			// Filter out special columns
			if (col.field === '__check__' || col.field === 'id') return false;
			// Check if column is visible (if not in model, it's visible by default)
			return columnVisibilityModel[col.field] !== false;
		});

		// Create worksheet data
		const worksheetData = [];

		// Add headers
		const headers = visibleColumns.map((col) => col.headerName || col.field);
		worksheetData.push(headers);

		// Add data rows
		rows.forEach((row) => {
			const rowData = visibleColumns.map((col) => {
				const value = row[col.field];
				// Handle formatted values
				if (col.valueFormatter) {
					return col.valueFormatter({ value, field: col.field, api: apiRef.current });
				}
				return value;
			});
			worksheetData.push(rowData);
		});

		// Create Excel file using simple CSV format with UTF-8 BOM for Excel compatibility
		const csvContent = worksheetData
			.map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
			.join('\n');

		// Add UTF-8 BOM for proper Excel encoding
		const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', `table_export_${new Date().getTime()}.csv`);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		handleClose();
	};

	const handleExportExcel = async () => {
		const XLSX = await import('xlsx');
		const columns = apiRef.current.getAllColumns();
		const rows = apiRef.current.getSortedRowIds().map((id) => apiRef.current.getRow(id));
		const columnVisibilityModel = apiRef.current.state.columns.columnVisibilityModel;

		const visibleColumns = columns.filter((col) => {
			if (col.field === '__check__' || col.field === 'id') return false;
			return columnVisibilityModel[col.field] !== false;
		});

		const headers = visibleColumns.map((col) => col.headerName || col.field);
		const worksheetData = [headers];
		rows.forEach((row) => {
			const rowData = visibleColumns.map((col) => {
				const value = row[col.field];
				if (col.valueFormatter) {
					return col.valueFormatter({ value, field: col.field, api: apiRef.current });
				}
				return value;
			});
			worksheetData.push(rowData);
		});

		const ws = XLSX.utils.aoa_to_sheet(worksheetData);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Export');
		XLSX.writeFile(wb, `table_export_${Date.now()}.xlsx`);

		handleClose();
	};

	/* PDF Export - Commented out to reduce bundle size
	 * To enable: uncomment imports at top and this function
	const handleExportPDF = () => {
		try {
			const columns = apiRef.current.getAllColumns();
			const rows = apiRef.current.getSortedRowIds().map((id) => apiRef.current.getRow(id));
			const columnVisibilityModel = apiRef.current.state.columns.columnVisibilityModel;

			// Get visible columns only
			const visibleColumns = columns.filter((col) => {
				// Filter out special columns
				if (col.field === '__check__' || col.field === 'id') return false;
				// Check if column is visible (if not in model, it's visible by default)
				return columnVisibilityModel[col.field] !== false;
			});

			// Prepare table data
			const headers = visibleColumns.map((col) => col.headerName || col.field);
			const data = rows.map((row) => {
				return visibleColumns.map((col) => {
					const value = row[col.field];
					// Handle formatted values
					if (col.valueFormatter) {
						return col.valueFormatter({ value, field: col.field, api: apiRef.current });
					}
					return value ?? '';
				});
			});

			// Create PDF
			const doc = new jsPDF({
				orientation: visibleColumns.length > 6 ? 'landscape' : 'portrait',
				unit: 'mm',
				format: 'a4',
			});

			// Use autotable function (jspdf-autotable v5.x syntax)
			autoTable(doc, {
				head: [headers],
				body: data,
				styles: {
					fontSize: 8,
					cellPadding: 2,
				},
				headStyles: {
					fillColor: [66, 66, 66],
					textColor: [255, 255, 255],
					fontStyle: 'bold',
				},
				margin: { top: 10 },
			});

			doc.save(`table_export_${new Date().getTime()}.pdf`);
		} catch (error) {
			console.error('Error generating PDF:', error);
			alert('Error generating PDF. Please try CSV export instead.');
		}

		handleClose();
	};
	*/

	return (
		<GridToolbarContainer>
			<GridToolbarColumnsButton />
			<GridToolbarFilterButton />
			<GridToolbarDensitySelector />

			{/* Print current page button */}
			{/* <Button
				size="small"
				startIcon={<PrintIcon />}
				onClick={handlePrint}
				sx={{ fontSize: '0.8125rem' }}
			>
				{localeText.toolbarExportPrint || 'Print'}
			</Button> */}

			{/* Custom Export Button */}
			<Button
				size="small"
				startIcon={<FileDownloadIcon />}
				onClick={handleExportClick}
				sx={{ fontSize: '0.8125rem' }}
			>
				{localeText.toolbarExport || 'Export'}
			</Button>
			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				<MenuItem onClick={handleExportExcel}>{localeText.toolbarExportExcel || 'Export as Excel'}</MenuItem>
				<MenuItem onClick={handleExportCSV}>{localeText.toolbarExportCSV || 'Export as CSV'}</MenuItem>
				{/* PDF export disabled to reduce bundle size */}
				{/* <MenuItem onClick={handleExportPDF}>{localeText.toolbarExportPrint || 'Print'}</MenuItem> */}
			</Menu>

			{selectedRowCount > 0 && (
				<>
					<Typography
						variant="caption"
						color="warning.main"
						sx={{ ml: 1, fontWeight: 600, whiteSpace: 'nowrap' }}
					>
						{selectedRowsWarning}
					</Typography>
					<Button size="small" onClick={handleClearSelection} sx={{ fontSize: '0.8125rem' }}>
						{localeText.toolbarClearSelection || 'Clear selection'}
					</Button>
				</>
			)}

			<GridToolbarQuickFilter sx={{ marginLeft: 'auto' }} />
		</GridToolbarContainer>
	);
}
